'use client';

// A product's buy links and the affiliate disclosure beside them, shared by
// the editor's product view and the public product page (plan item S24) so
// the offer rules live in one place:
//
//   - the disclosure sits directly above the first link and links to
//     /disclosure (docs/plans/2026-09-14-av-gear-spec.md §8.6);
//   - Amazon only with a verified ASIN, and never inside the native app;
//   - in the app every link leaves through `useNativeHost().openExternal`;
//   - `rel="sponsored"`, `data-commerce` on the block, `gear_offer_clicked`.
//
// The surface passes the text role its disclosure line takes (the editor's
// caption, a public page's second line); everything else is identical.

import { amazonUrl, ebayUsedUrl, type Product } from '@roomtreatment/av-catalog';
import Button, { buttonClass, type ButtonSize, type ButtonVariant } from '@/components/ui/Button';
import { EVENTS, track } from '@/lib/analytics';
import { useNativeHost } from '../state/NativeHostContext';
import { OFFER_DISCLOSURE } from './labels';

interface Props {
  product: Product;
  /** The surface's second-line text role, e.g. `ed-caption`. */
  captionClass: string;
  /** The surface's inline link style for "Disclosure". */
  linkClass?: string;
  size?: ButtonSize;
  className?: string;
}

export default function GearOffers({ product, captionClass, linkClass = 'underline underline-offset-2', size = 'sm', className = '' }: Props) {
  const { isNative, openExternal } = useNativeHost();
  const amazon = amazonUrl(product);
  const showAmazon = !!amazon && !isNative;

  return (
    <div className={className} data-commerce data-testid="gear-offers">
      <p className={captionClass} data-testid="gear-offer-disclosure">
        {OFFER_DISCLOSURE}{' '}
        {/* A new tab on the web, so reading it never leaves an open room. */}
        <a href="/disclosure" className={linkClass} {...(isNative ? {} : { target: '_blank', rel: 'noopener' })}>
          Disclosure
        </a>
      </p>
      <div className="flex flex-wrap gap-2">
        {showAmazon && (
          <BuyLink label="Amazon" url={amazon} retailer="amazon" product={product} variant="secondary" size={size} isNative={isNative} openExternal={openExternal} />
        )}
        <BuyLink
          label="Used on eBay"
          url={ebayUsedUrl(product)}
          retailer="ebay"
          product={product}
          variant={showAmazon ? 'ghost' : 'secondary'}
          size={size}
          isNative={isNative}
          openExternal={openExternal}
        />
      </div>
    </div>
  );
}

function BuyLink({
  label,
  url,
  retailer,
  product,
  variant,
  size,
  isNative,
  openExternal,
}: {
  label: string;
  url: string;
  retailer: 'amazon' | 'ebay';
  product: Product;
  variant: ButtonVariant;
  size: ButtonSize;
  isNative: boolean;
  openExternal: (url: string) => void;
}) {
  const fire = () => track(EVENTS.gear_offer_clicked, { product_id: product.id, retailer });
  if (isNative) {
    return (
      <Button
        variant={variant}
        size={size}
        data-testid={`gear-buy-${retailer}`}
        onClick={() => {
          fire();
          openExternal(url);
        }}
      >
        {label}
      </Button>
    );
  }
  return (
    <a
      data-testid={`gear-buy-${retailer}`}
      href={url}
      target="_blank"
      rel="sponsored noopener noreferrer"
      className={buttonClass({ variant, size })}
      onClick={fire}
    >
      {label}
    </a>
  );
}
